import { FC } from "preact/compat";
import { useEffect } from "preact/hooks";
import { useSignal } from "@preact/signals";
import { Navigate, createBrowserRouter } from "react-router-dom";
import { Admin, AdminRedirect, Main, Public, Redirect } from "~/layouts";
import Login from "~/routes/login";
import { LoadingProgress, RouteLoading } from "./components";

const LazyRoute: FC<{
    importer: () => Promise<{ default: FC }>;
    importKey: string;
}> = ({ importer, importKey }) => {
    const component = useSignal<FC | null>(null);
    const loading = useSignal(true);

    useEffect(() => {
        loading.value = true;

        importer().then((mod) => {
            component.value = mod.default;
            loading.value = false;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [importKey]);

    const Component = component.value || RouteLoading;

    return (
        <>
            <Component />
            {loading.value && <LoadingProgress />}
        </>
    );
};

const router = createBrowserRouter([
    {
        path: "/me/:installation/:company",
        element: <Main />,
        children: [
            {
                index: true,
                element: <Navigate to="dashboard" replace />,
            },
            {
                path: "dashboard",
                element: (
                    <LazyRoute
                        importKey="dashboard"
                        importer={() => import("~/routes/dashboard")}
                    />
                ),
            },
            {
                path: "year-overview",
                element: (
                    <LazyRoute
                        importKey="year-overview"
                        importer={() => import("~/routes/year-overview")}
                    />
                ),
            },
            {
                path: "archive",
                element: (
                    <LazyRoute
                        importKey="archive"
                        importer={() => import("~/routes/archive")}
                    />
                ),
            },
            {
                path: "admin",
                element: <Admin />,
                children: [
                    {
                        index: true,
                        element: <AdminRedirect />,
                    },
                    {
                        path: "time",
                        element: (
                            <LazyRoute
                                importKey="admin-time"
                                importer={() => import("~/routes/admin-time")}
                            />
                        ),
                    },
                    {
                        path: "approval",
                        element: (
                            <LazyRoute
                                importKey="admin-approval"
                                importer={() =>
                                    import("~/routes/admin-approval")
                                }
                            />
                        ),
                    },
                    {
                        path: "expenses",
                        element: (
                            <LazyRoute
                                importKey="admin-expenses"
                                importer={() =>
                                    import("~/routes/admin-expenses")
                                }
                            />
                        ),
                    },
                    {
                        path: "tasks",
                        element: (
                            <LazyRoute
                                importKey="admin-tasks"
                                importer={() => import("~/routes/admin-tasks")}
                            />
                        ),
                    },
                ],
            },
            {
                path: "projects",
                element: (
                    <LazyRoute
                        importKey="projects"
                        importer={() => import("~/routes/projects")}
                    />
                ),
            },
            {
                path: "projects/:project/:tab?",
                element: (
                    <LazyRoute
                        importKey="project"
                        importer={() => import("~/routes/project")}
                    />
                ),
            },
            {
                path: "resources/:tab?",
                element: (
                    <LazyRoute
                        importKey="resources"
                        importer={() => import("~/routes/resources")}
                    />
                ),
            },
            {
                path: "chat",
                element: (
                    <LazyRoute
                        importKey="chat"
                        importer={() => import("~/routes/chat")}
                    />
                ),
            },
            {
                path: "settings",
                element: (
                    <LazyRoute
                        importKey="settings"
                        importer={() => import("~/routes/settings")}
                    />
                ),
            },
            {
                path: "profile",
                element: (
                    <LazyRoute
                        importKey="profile"
                        importer={() => import("~/routes/profile")}
                    />
                ),
            },
        ],
    },
    {
        path: "/p",
        element: <Public />,
        children: [
            { index: true, element: <Navigate to="/p/login" replace /> },
            {
                path: "login",
                element: <Login />,
            },
        ],
    },
    {
        path: "*",
        element: <Redirect />,
    },
]);

export default router;
